import React, { useState, useEffect, useContext } from 'react';
import axios from '../../axiosConfig';
import { TrainerContext } from '../../context/TrainerContext';
import { isOwner } from '../../utils/trainerRoleUtils';
import '../../styles/SchoolData.css';

const SchoolData = ({ schoolId }) => {
    const { trainer } = useContext(TrainerContext);
    const currentUserRole = trainer?.trainerSchoolRole;
    const userIsOwner = isOwner(currentUserRole);

    const [school, setSchool] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        address: {
            country: '',
            city: '',
            postalCode: '',
            street: '',
            buildingNumber: '',
            apartmentNumber: '',
        },
    });
    const [halls, setHalls] = useState([]);
    const [newHallName, setNewHallName] = useState('');
    const [showAddHallForm, setShowAddHallForm] = useState(false);
    const [editHallId, setEditHallId] = useState(null);
    const [editHallName, setEditHallName] = useState('');

    // Invoice data state (only for owners)
    const [invoiceData, setInvoiceData] = useState(null);
    const [invoiceEditMode, setInvoiceEditMode] = useState(false);
    const [invoiceFormData, setInvoiceFormData] = useState({
        countryCode: 'PL',
        companyName: '',
        nip: '',
        regon: '',
        street: '',
        buildingNumber: '',
        apartmentNumber: '',
        postalCode: '',
        city: '',
        country: 'Polska',
    });

    useEffect(() => {
        if (!schoolId) return;
        const token = localStorage.getItem('jwt');
        axios
            .get(`/api/v1/school/${schoolId}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((response) => {
                setSchool(response.data);
                setFormData(response.data);
                setLoading(false);
            })
            .catch(() => {
                setError('Błąd pobierania danych szkoły.');
                setLoading(false);
            });

        axios
            .get(`/api/v1/school/${schoolId}/halls`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((response) => {
                setHalls(response.data || []);
            })
            .catch(() => {
                setHalls([]);
            });
    }, [schoolId]);

    // Fetch invoice data for owners
    useEffect(() => {
        if (!schoolId || !userIsOwner) return;
        const token = localStorage.getItem('jwt');
        axios
            .get(`/api/v1/school/${schoolId}/invoice-data`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((response) => {
                if (response.status === 200 && response.data) {
                    setInvoiceData(response.data);
                    setInvoiceFormData(response.data);
                }
            })
            .catch(() => {
                // No invoice data yet - that's ok
                setInvoiceData(null);
            });
    }, [schoolId, userIsOwner]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleAddressChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            address: {
                ...prevData.address,
                [name]: value,
            },
        }));
    };

    const handleSubmit = () => {
        const token = localStorage.getItem('jwt');
        axios
            .patch(`/api/v1/school/${schoolId}`, formData, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(() => {
                setSchool(formData);
                setEditMode(false);
                alert('Dane szkoły zostały pomyślnie zaktualizowane.');
            })
            .catch(() => {
                setError('Błąd przy aktualizacji danych szkoły.');
            });
    };

    const handleAddHall = () => {
        if (!newHallName.trim()) {
            setError('Nazwa sali jest wymagana.');
            return;
        }

        const token = localStorage.getItem('jwt');
        axios
            .post(`/api/v1/school/${schoolId}/halls`, { name: newHallName }, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((response) => {
                setHalls([...halls, response.data]);
                setNewHallName('');
                setShowAddHallForm(false); // Hide form after successful add
                setError('');
            })
            .catch(() => {
                setError('Błąd przy dodawaniu sali.');
            });
    };

    const handleCancelAddHall = () => {
        setNewHallName('');
        setShowAddHallForm(false);
        setError('');
    };

    const handleEditHall = (hallId) => {
        if (!hallId) return;
        const token = localStorage.getItem('jwt');
        axios
            .patch(`/api/v1/school/halls/${hallId}`, { name: editHallName }, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((response) => {
                setHalls(halls.map(hall => hall.id === hallId ? response.data : hall));
                setEditHallId(null);
                setEditHallName('');
            })
            .catch(() => {
                setError('Błąd przy edycji sali.');
            });
    };

    const handleDeleteHall = (hallId) => {
        if (!hallId) return;
        const token = localStorage.getItem('jwt');
        axios
            .delete(`/api/v1/school/halls/${hallId}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(() => {
                setHalls(halls.filter(hall => hall.id !== hallId));
            })
            .catch(() => {
                setError('Błąd przy usuwaniu sali.');
            });
    };

    // Invoice data handlers
    const handleInvoiceChange = (e) => {
        const { name, value } = e.target;
        setInvoiceFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleInvoiceSubmit = () => {
        const token = localStorage.getItem('jwt');
        axios
            .put(`/api/v1/school/${schoolId}/invoice-data`, invoiceFormData, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((response) => {
                setInvoiceData(response.data);
                setInvoiceEditMode(false);
                alert('Dane do faktury zostały pomyślnie zapisane.');
            })
            .catch(() => {
                setError('Błąd przy zapisywaniu danych do faktury.');
            });
    };

    const handleInvoiceCancel = () => {
        if (invoiceData) {
            setInvoiceFormData(invoiceData);
        } else {
            setInvoiceFormData({
                countryCode: 'PL',
                companyName: '',
                nip: '',
                regon: '',
                street: '',
                buildingNumber: '',
                apartmentNumber: '',
                postalCode: '',
                city: '',
                country: 'Polska',
            });
        }
        setInvoiceEditMode(false);
    };

    const handleInvoiceDelete = () => {
        if (!window.confirm('Czy na pewno chcesz usunąć dane do faktury?')) return;
        const token = localStorage.getItem('jwt');
        axios
            .delete(`/api/v1/school/${schoolId}/invoice-data`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(() => {
                setInvoiceData(null);
                setInvoiceFormData({
                    countryCode: 'PL',
                    companyName: '',
                    nip: '',
                    regon: '',
                    street: '',
                    buildingNumber: '',
                    apartmentNumber: '',
                    postalCode: '',
                    city: '',
                    country: 'Polska',
                });
                alert('Dane do faktury zostały usunięte.');
            })
            .catch(() => {
                setError('Błąd przy usuwaniu danych do faktury.');
            });
    };

    if (loading) return <div className="school-data__loading">Ładowanie danych szkoły...</div>;
    if (error) return <div className="school-data__error">{error}</div>;
    if (!school) return null;

    const {
        name: schoolName,
        email,
        phone,
        address: { country, city, postalCode, street, buildingNumber, apartmentNumber },
    } = formData;

    return (
        <div className="school-data">
            {/* Basic School Information Section */}
            <div className="school-data__section">
                <div className="school-data__section-header">
                    <h3 className="school-data__section-title">Informacje o szkole</h3>
                    {editMode ? (
                        <div className="school-data__header-buttons">
                            <button className="school-data__btn school-data__btn--small school-data__btn--primary" onClick={handleSubmit}>
                                Zapisz
                            </button>
                            <button className="school-data__btn school-data__btn--small school-data__btn--secondary" onClick={() => setEditMode(false)}>
                                Anuluj
                            </button>
                        </div>
                    ) : (
                        <button className="school-data__btn school-data__btn--small school-data__btn--primary" onClick={() => setEditMode(true)}>
                            Edytuj
                        </button>
                    )}
                </div>

                <div className="school-data__row school-data__row--basic-info">
                    <div className="school-data__field-group school-data__field-group--wide">
                        <label className="school-data__label">Nazwa szkoły</label>
                        <input
                            className="school-data__input"
                            name="name"
                            type="text"
                            value={schoolName}
                            onChange={handleChange}
                            disabled={!editMode}
                            placeholder="Nazwa szkoły"
                        />
                    </div>
                    <div className="school-data__field-group">
                        <label className="school-data__label">Email</label>
                        <input
                            className="school-data__input"
                            name="email"
                            type="text"
                            value={email}
                            onChange={handleChange}
                            disabled={!editMode}
                            placeholder="Email szkoły"
                        />
                    </div>
                    <div className="school-data__field-group">
                        <label className="school-data__label">Telefon</label>
                        <input
                            className="school-data__input"
                            name="phone"
                            type="text"
                            value={phone}
                            onChange={handleChange}
                            disabled={!editMode}
                            placeholder="Numer telefonu"
                        />
                    </div>
                </div>
            </div>

            {/* Address Information Section */}
            <div className="school-data__section">
                <h3 className="school-data__section-title">Adres szkoły</h3>

                <div className="school-data__row school-data__row--address-info">
                    <div className="school-data__field-group">
                        <label className="school-data__label">Kraj</label>
                        <input
                            className="school-data__input"
                            name="country"
                            type="text"
                            value={country}
                            onChange={handleAddressChange}
                            disabled={!editMode}
                            placeholder="Kraj"
                        />
                    </div>
                    <div className="school-data__field-group">
                        <label className="school-data__label">Miasto</label>
                        <input
                            className="school-data__input"
                            name="city"
                            type="text"
                            value={city}
                            onChange={handleAddressChange}
                            disabled={!editMode}
                            placeholder="Miasto"
                        />
                    </div>
                    <div className="school-data__field-group">
                        <label className="school-data__label">Kod pocztowy</label>
                        <input
                            className="school-data__input"
                            name="postalCode"
                            type="text"
                            value={postalCode}
                            onChange={handleAddressChange}
                            disabled={!editMode}
                            placeholder="Kod pocztowy"
                        />
                    </div>
                </div>

                <div className="school-data__row school-data__row--address-info">
                    <div className="school-data__field-group">
                        <label className="school-data__label">Ulica</label>
                        <input
                            className="school-data__input"
                            name="street"
                            type="text"
                            value={street}
                            onChange={handleAddressChange}
                            disabled={!editMode}
                            placeholder="Ulica"
                        />
                    </div>
                    <div className="school-data__field-group">
                        <label className="school-data__label">Numer budynku</label>
                        <input
                            className="school-data__input"
                            name="buildingNumber"
                            type="text"
                            value={buildingNumber}
                            onChange={handleAddressChange}
                            disabled={!editMode}
                            placeholder="Numer budynku"
                        />
                    </div>
                    <div className="school-data__field-group">
                        <label className="school-data__label">Numer mieszkania</label>
                        <input
                            className="school-data__input"
                            name="apartmentNumber"
                            type="text"
                            value={apartmentNumber}
                            onChange={handleAddressChange}
                            disabled={!editMode}
                            placeholder="Numer mieszkania"
                        />
                    </div>
                </div>
            </div>

            {/* Halls Section */}
            <div className="school-data__section school-data__halls-section">
                <h3 className="school-data__section-title">Sale</h3>

                <div className="school-data__halls-grid">
                    {halls.map((hall) => (
                        <div key={hall.id} className="school-data__hall-card">
                            {editHallId === hall.id ? (
                                <div className="school-data__hall-edit-content">
                                    <input
                                        className="school-data__hall-edit-input"
                                        type="text"
                                        value={editHallName}
                                        onChange={(e) => setEditHallName(e.target.value)}
                                        placeholder="Edytuj nazwę sali"
                                    />
                                    <div className="school-data__hall-edit-actions">
                                        <button
                                            className="school-data__btn school-data__btn--small school-data__btn--primary"
                                            onClick={() => handleEditHall(hall.id)}
                                        >
                                            Zapisz
                                        </button>
                                        <button
                                            className="school-data__btn school-data__btn--small school-data__btn--secondary"
                                            onClick={() => setEditHallId(null)}
                                        >
                                            Anuluj
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="school-data__hall-content">
                                    <div className="school-data__hall-name">{hall.name}</div>
                                    <div className="school-data__hall-actions">
                                        <div
                                            className="school-data__hall-icon school-data__hall-edit-icon"
                                            onClick={() => {
                                                if (hall.id) {
                                                    setEditHallId(hall.id);
                                                    setEditHallName(hall.name);
                                                } else {
                                                    console.error("Hall id is undefined");
                                                }
                                            }}
                                            title="Edytuj"
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M14.06 9.02l.92.92L5.92 19H5v-.92l9.06-9.06M17.66 3c-.25 0-.51.1-.7.29l-1.83 1.83 3.75 3.75 1.83-1.83c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.2-.2-.45-.29-.71-.29zm-3.6 3.19L3 17.25V21h3.75L17.81 9.94l-3.75-3.75z"/>
                                            </svg>
                                        </div>
                                        <div
                                            className="school-data__hall-icon school-data__hall-delete-icon"
                                            onClick={() => handleDeleteHall(hall.id)}
                                            title="Usuń"
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M19 4h-3.5l-1-1h-5l-1 1H5v2h14M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6v12z"/>
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Add new hall card - conditional rendering */}
                    <div className="school-data__hall-card school-data__hall-card--add">
                        {!showAddHallForm ? (
                            /* Plus icon state */
                            <div className="school-data__add-hall-plus" onClick={() => setShowAddHallForm(true)}>
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                                </svg>
                            </div>
                        ) : (
                            /* Form state */
                            <div className="school-data__new-hall-content">
                                <input
                                    className="school-data__new-hall-input"
                                    type="text"
                                    value={newHallName}
                                    onChange={(e) => setNewHallName(e.target.value)}
                                    placeholder="Nazwa sali..."
                                />
                                <div className="school-data__add-hall-buttons">
                                    <button
                                        className="school-data__btn school-data__btn--primary school-data__btn--add-hall"
                                        onClick={handleAddHall}
                                    >
                                        Dodaj salę
                                    </button>
                                    <button
                                        className="school-data__btn school-data__btn--secondary school-data__btn--add-hall"
                                        onClick={handleCancelAddHall}
                                    >
                                        Anuluj
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Invoice Data Section - Only for school owners */}
            {userIsOwner && (
                <div className="school-data__section school-data__invoice-section">
                    <div className="school-data__section-header">
                        <h3 className="school-data__section-title">Dane do faktury</h3>
                        {invoiceEditMode ? (
                            <div className="school-data__header-buttons">
                                <button
                                    className="school-data__btn school-data__btn--small school-data__btn--primary"
                                    onClick={handleInvoiceSubmit}
                                >
                                    Zapisz
                                </button>
                                <button
                                    className="school-data__btn school-data__btn--small school-data__btn--secondary"
                                    onClick={handleInvoiceCancel}
                                >
                                    Anuluj
                                </button>
                            </div>
                        ) : (
                            <div className="school-data__header-buttons">
                                <button
                                    className="school-data__btn school-data__btn--small school-data__btn--primary"
                                    onClick={() => setInvoiceEditMode(true)}
                                >
                                    {invoiceData ? 'Edytuj' : 'Dodaj'}
                                </button>
                                {invoiceData && (
                                    <button
                                        className="school-data__btn school-data__btn--small school-data__btn--danger"
                                        onClick={handleInvoiceDelete}
                                    >
                                        Usuń
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {!invoiceData && !invoiceEditMode ? (
                        <div className="school-data__invoice-empty">
                            <div className="school-data__invoice-empty-icon">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11zM8 15.01V17h2v-1.99H8zm0-4V13h2v-1.99H8zm4 4V17h5v-1.99h-5zm0-4V13h5v-1.99h-5z"/>
                                </svg>
                            </div>
                            <p className="school-data__invoice-empty-text">
                                Nie dodano jeszcze danych do faktury
                            </p>
                            <button
                                className="school-data__btn school-data__btn--primary"
                                onClick={() => setInvoiceEditMode(true)}
                            >
                                Dodaj dane do faktury
                            </button>
                        </div>
                    ) : (
                        <div className="school-data__invoice-card">
                            {/* Company Info */}
                            <div className="school-data__invoice-group">
                                <h4 className="school-data__invoice-group-title">Dane firmy</h4>
                                <div className="school-data__row school-data__row--invoice">
                                    <div className="school-data__field-group">
                                        <label className="school-data__label">Nazwa firmy *</label>
                                        <input
                                            className="school-data__input"
                                            name="companyName"
                                            type="text"
                                            value={invoiceFormData.companyName || ''}
                                            onChange={handleInvoiceChange}
                                            disabled={!invoiceEditMode}
                                            placeholder="Nazwa firmy"
                                        />
                                    </div>
                                    <div className="school-data__field-group">
                                        <label className="school-data__label">NIP</label>
                                        <input
                                            className="school-data__input"
                                            name="nip"
                                            type="text"
                                            value={invoiceFormData.nip || ''}
                                            onChange={handleInvoiceChange}
                                            disabled={!invoiceEditMode}
                                            placeholder="0000000000"
                                        />
                                    </div>
                                    <div className="school-data__field-group">
                                        <label className="school-data__label">REGON</label>
                                        <input
                                            className="school-data__input"
                                            name="regon"
                                            type="text"
                                            value={invoiceFormData.regon || ''}
                                            onChange={handleInvoiceChange}
                                            disabled={!invoiceEditMode}
                                            placeholder="000000000"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Address */}
                            <div className="school-data__invoice-group">
                                <h4 className="school-data__invoice-group-title">Adres</h4>
                                <div className="school-data__row school-data__row--invoice">
                                    <div className="school-data__field-group">
                                        <label className="school-data__label">Ulica *</label>
                                        <input
                                            className="school-data__input"
                                            name="street"
                                            type="text"
                                            value={invoiceFormData.street || ''}
                                            onChange={handleInvoiceChange}
                                            disabled={!invoiceEditMode}
                                            placeholder="Ulica"
                                        />
                                    </div>
                                    <div className="school-data__field-group">
                                        <label className="school-data__label">Numer budynku</label>
                                        <input
                                            className="school-data__input"
                                            name="buildingNumber"
                                            type="text"
                                            value={invoiceFormData.buildingNumber || ''}
                                            onChange={handleInvoiceChange}
                                            disabled={!invoiceEditMode}
                                            placeholder="Nr budynku"
                                        />
                                    </div>
                                    <div className="school-data__field-group">
                                        <label className="school-data__label">Numer lokalu</label>
                                        <input
                                            className="school-data__input"
                                            name="apartmentNumber"
                                            type="text"
                                            value={invoiceFormData.apartmentNumber || ''}
                                            onChange={handleInvoiceChange}
                                            disabled={!invoiceEditMode}
                                            placeholder="Nr lokalu"
                                        />
                                    </div>
                                </div>
                                <div className="school-data__row school-data__row--invoice">
                                    <div className="school-data__field-group">
                                        <label className="school-data__label">Kod pocztowy *</label>
                                        <input
                                            className="school-data__input"
                                            name="postalCode"
                                            type="text"
                                            value={invoiceFormData.postalCode || ''}
                                            onChange={handleInvoiceChange}
                                            disabled={!invoiceEditMode}
                                            placeholder="00-000"
                                        />
                                    </div>
                                    <div className="school-data__field-group">
                                        <label className="school-data__label">Miasto *</label>
                                        <input
                                            className="school-data__input"
                                            name="city"
                                            type="text"
                                            value={invoiceFormData.city || ''}
                                            onChange={handleInvoiceChange}
                                            disabled={!invoiceEditMode}
                                            placeholder="Miasto"
                                        />
                                    </div>
                                    <div className="school-data__field-group">
                                        <label className="school-data__label">Kraj *</label>
                                        <input
                                            className="school-data__input"
                                            name="country"
                                            type="text"
                                            value={invoiceFormData.country || ''}
                                            onChange={handleInvoiceChange}
                                            disabled={!invoiceEditMode}
                                            placeholder="Polska"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SchoolData;